import { ReactElement, useCallback } from 'react';
import { usePrevious, useUpdateEffect } from 'react-use';

import { DayOfWeek, TimeOfDay } from '@dreryk/egabinet-waitlist-frontend-api';

import { BindingCreator, commonTranslationKeys } from '@dreryk/react-common';
import { Multiselect, UncontrolledCheckbox } from '@dreryk/react-components';

import { WaitlistFormController } from '../../WaitlistForm.types';
import { translationKeys } from '../../../translations';

import { useTriggerLabelExtractor, useValidationRules } from './DayPreferenceField.hooks';
import { StyledDayPreferenceFieldMultiselect } from './DayPreferenceFieldMultiselect.styled';
import { DayPreferenceFormBeanPart } from './DayPreferenceField.types';
import { TIMES_OF_DAY } from './DayPreferenceField.constants';

type DayPreferenceFieldProps<DAY_OF_WEEK extends DayOfWeek> = Pick<
   WaitlistFormController,
   'watch' | 'formState' | 'trigger' | 'setValue'
> & {
   register: BindingCreator<DayPreferenceFormBeanPart<DAY_OF_WEEK>>;
   dayOfWeek: DAY_OF_WEEK;
   onChange: VoidFunction;
};

export const DayPreferenceField = <DAY_OF_WEEK extends DayOfWeek>(
   props: DayPreferenceFieldProps<DAY_OF_WEEK>
): ReactElement => {
   const fieldName = `dayTimePreferences.${props.dayOfWeek}` as const;
   const validationRules = useValidationRules<DAY_OF_WEEK>();

   const { setValue, watch, onChange, trigger } = props;

   const fieldError = props.formState.errors.dayTimePreferences?.[props.dayOfWeek];

   // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
   const currentPreferences = watch(fieldName) as TimeOfDay[];
   const previousPreferences = usePrevious(currentPreferences);

   useUpdateEffect(() => {
      if (currentPreferences.length !== previousPreferences?.length) {
         trigger(fieldName);
      }
   }, [currentPreferences]);

   const handleDayChange = useCallback(
      (selected: boolean) => {
         setValue(fieldName as string, selected ? TIMES_OF_DAY : [], {
            shouldDirty: true,
            shouldValidate: true,
         });
         onChange();
      },
      [fieldName, onChange, setValue]
   );

   const triggerLabelExtractor = useTriggerLabelExtractor();

   return (
      <>
         <UncontrolledCheckbox
            id={props.dayOfWeek}
            name={props.dayOfWeek}
            onSelectionChange={handleDayChange}
            selected={currentPreferences.length > 0}
            label={commonTranslationKeys.dayOfWeekMap[props.dayOfWeek]}
            testId={props.dayOfWeek}
            error={Boolean(fieldError)}
         />
         <StyledDayPreferenceFieldMultiselect
            testId={props.dayOfWeek}
            triggerLabelExtractor={triggerLabelExtractor}
            controller={props.register(fieldName, validationRules)}
            placeholder={translationKeys.choosePlaceholder.standard}
            onItemSelect={onChange}
         >
            <>
               <Multiselect.SelectAll
                  testId={'allPreferences'}
                  selectAllLabel={translationKeys.chooseAll}
                  items={TIMES_OF_DAY}
               />
               <Multiselect.List items={TIMES_OF_DAY}>
                  {(item) => <Multiselect.Item testId={item} item={item} label={translationKeys.timeOfDayMap[item]} />}
               </Multiselect.List>
            </>
         </StyledDayPreferenceFieldMultiselect>
      </>
   );
};
